import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type GetManyReferenceParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  CompanyNextStep,
  ContactNote,
  Deal,
  DealNote,
  DialogInvitation,
  GlobalSearchHit,
  FieldDefChangeInput,
  FieldDefChangeResult,
  NextStepInput,
  LogContactInput,
  LogContactResult,
  RAFile,
  Sale,
  TimeEntry,
  SalesFormData,
  SignUpData,
  WorkItem,
  DealFromLostInput,
  MarkDealLostInput,
  MarkDealWonInput,
  WonDealFootprint,
  CleanupWonDealInput,
  PostponeDealInput,
  BrregSearchHit,
  CreateLeadInput,
  CreateLeadResult,
  LeadDuplicate,
  LeadDuplicateInput,
  RetentionPreview,
  LiveQueryResult,
  ApproveProposalResult,
  PageCoverage,
  CustomerPageStatus,
  PageProposalEdit,
  PageProposalPayload,
  PageSource,
  PageTargetType,
  LiveQuestionDraft,
  PublicQuote,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import type {
  BillingPeriod,
  BillingPreview,
  ExportKind,
  ExportOutcome,
  ExportRequest,
} from "@shared/billing.ts";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import { stripClientFilters } from "../commons/clientFilters";
import { getIsInitialized } from "./authProvider";
import { getSupabaseClient } from "./supabase";
import { applyFullTextSearch, stripUnhandledQ } from "./applyFullTextSearch";
import { normalizeArrayFilters } from "./arrayFilters";
import { type CostClient, withPrivateCosts } from "./privateCosts";
import {
  createErrorReporter,
  type Rpc,
  withErrorInsight,
} from "./errorInsight";
import { classifyError } from "@/lib/errorInsight";
import {
  type Fasetter,
  TOMME_FASETTER,
} from "../../prospects/prospektListeQuery";
// Delt med importer-leads.mjs: innlastingen fra appen skal skrive NØYAKTIG det
// samme som skriptet gjør. Se lesProffFil.mjs.
import {
  grupperPaaUtfylteFelt,
  radForUpsert,
} from "../../../../../scripts/leads/normalisering.mjs";
import { isNetworkFailure, UnsettledError } from "../../workItems/timerSync";
import {
  dealStageFromRow,
  isDealOpen,
  type DealStageRow,
} from "../../deals/dealStatus";
import type { ProffRad } from "../../../../../scripts/leads/lesProffFil.mjs";

// Hent en lesbar feilmelding fra en edge-funksjon-feil (FunctionsHttpError
// legger detaljene i context som en Response).
export type EmailRecipientPreview = {
  recipients: { email: string; navn: string; companyId: number | null }[];
  excluded: {
    email: string | null;
    navn: string;
    companyId: number | null;
    reason: "optout" | "bounced" | "missing_email" | "archived" | "duplicate";
  }[];
};

const invokeError = async (
  error: unknown,
  fallback: string,
): Promise<string> => {
  try {
    const ctx = (
      error as { context?: { json?: () => Promise<{ message?: string }> } }
    )?.context;
    const body = ctx?.json ? await ctx.json() : undefined;
    return body?.message || fallback;
  } catch {
    return fallback;
  }
};

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
    supabaseClient: getSupabaseClient(),
    sortOrder: "asc,desc.nullslast" as any,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

// Produktbilde: last opp nye filer til bucketen (som kundelogoen). Bilder
// synket fra Tripletex ligger der allerede og passerer urørt.
const processProductImage = async (params: any) => {
  const image = params.data.image;

  if (image?.rawFile instanceof File) {
    await uploadToBucket(image);
  }

  return {
    ...params,
    data: {
      ...params.data,
      image,
    },
  };
};

// Uten svar fra serveren (status 0) er utfallet ukjent: «Uavklart».
const timerError = (
  error: { message?: string },
  status: number,
  fallback: string,
) => {
  const message = error.message || fallback;
  if (!status || isNetworkFailure(error)) return new UnsettledError(message);
  return Object.assign(new Error(message), { status });
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();

  return {
    ...baseDataProvider,
    async getManyReference(resource: string, params: GetManyReferenceParams) {
      return baseDataProvider.getManyReference(
        resource,
        normalizeArrayFilters(params),
      );
    },
    async getList(resource: string, params: GetListParams) {
      // Vern (PR-S1): q som overlever lifecycle-callbackene har ingen
      // søkekonfigurasjon og ville gitt 400 fra PostgREST.
      params = stripUnhandledQ(resource, params);
      params = stripClientFilters(resource, params);
      // En liste som filterverdi blir `in.(…)`, ikke `eq.1,2,3` (400).
      params = normalizeArrayFilters(params);
      // Virtuelt statusfilter i oppgavelisten (PR-S5): «åpne/fullførte»
      // er completed_at is/not.is null i PostgREST, ikke en kolonneverdi.
      if (resource === "work_items" && params.filter?.status) {
        const { status, ...filter } = params.filter as Record<string, unknown>;
        params = {
          ...params,
          filter: {
            ...filter,
            ...(status === "open" ? { "completed_at@is": "null" } : {}),
            ...(status === "done" ? { "completed_at@not.is": "null" } : {}),
          },
        };
      }
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            contact_note: undefined,
            deal_note: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }

      return baseDataProvider.getOne(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
      const response = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
          },
        },
      });

      if (!response.data?.user || response.error) {
        console.error("signUp.error", response.error);
        throw new Error(response?.error?.message || "Failed to create account");
      }

      // Update the is initialized cache
      (getIsInitialized as any)._is_initialized_cache = true;

      return {
        id: response.data.user.id,
        email,
        password,
      };
    },
    async salesCreate(body: SalesFormData) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: Sale;
      }>("users", {
        method: "POST",
        body,
      });

      if (!data || error) {
        console.error("salesCreate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw new Error(errorDetails?.message || "Failed to create the user");
      }

      // Timepris og kostpris lagres av users-funksjonen (service-rolle) i
      // sales_rates, som bare administrator leser og klienten ikke skriver.
      return data.data;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const {
        email,
        first_name,
        last_name,
        administrator,
        avatar,
        disabled,
        hourly_rate,
        hourly_cost,
        planned_week_hours,
      } = data;

      // Timepris og kostpris lagres av users-funksjonen (service-rolle) i
      // sales_rates, som bare administrator leser og klienten ikke skriver.
      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            first_name,
            last_name,
            administrator,
            disabled,
            avatar,
            hourly_rate,
            hourly_cost,
          },
        });

      if (!updatedData || error) {
        console.error("salesUpdate.error", error);
        throw new Error("Failed to update account manager");
      }
      // Planlagt uke lagres med egen RPC (Team på Oversikt).
      if (planned_week_hours != null) {
        const { error: hoursError } = await getSupabaseClient().rpc(
          "set_planned_week_hours",
          { p_sales_id: id, p_hours: Number(planned_week_hours) },
        );
        if (hoursError) {
          throw new Error(
            hoursError.message || "Kunne ikke lagre planlagt uke",
          );
        }
      }

      return updatedData.data;
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      // (bare åpne står i en kolonne på tavla; en avsluttet mulighet hentes
      // bare tilbake fra arkivet)
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: isDealOpen(deal)
          ? { stage: deal.stage, status: "open" }
          : { id: deal.id },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      // Bare rekkefølge og arkivering skrives: hele raden har generte
      // kolonner (annual_value) som databasen ikke tar imot.
      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: {
              index: updatedDeal.index,
              archived_at: updatedDeal.archived_at ?? null,
            },
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    // Send en enkel individuell e-post til kontaktene på de valgte kundene.
    // companyVars: fletteverdier per kunde (nøkkel = company_id som streng),
    // brukt av Dialog for {dialoglenke}.
    async sendBulkEmail(
      companyIds: Identifier[],
      subject: string,
      body: string,
      companyVars?: Record<string, Record<string, string>>,
      /** recipients: "company_email" sender bare til kundens eget e-postfelt (Dialog). */
      valg?: { recipients?: "company_email" },
    ) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        sent: number;
        failed: number;
        recipients: number;
        skipped_optout: number;
        failedEmails: string[];
        sent_company_ids: number[];
      }>("send_email", {
        method: "POST",
        body: {
          company_ids: companyIds,
          subject,
          body,
          ...(companyVars ? { company_vars: companyVars } : {}),
          ...(valg?.recipients ? { recipients: valg.recipients } : {}),
        },
      });

      if (!data || error) {
        const details = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw new Error(details?.message || "Kunne ikke sende e-post");
      }

      return data;
    },
    // Send én testmelding til den innloggede brukeren selv.
    // P16-10 (US25.1): hvem som får utsendingen og hvem som utelates, med
    // årsak. Samme regler som sendingen; sender ingenting.
    async previewEmailRecipients(
      companyIds: Identifier[],
      valg?: { recipients?: "company_email" },
    ): Promise<EmailRecipientPreview> {
      const { data, error } =
        await getSupabaseClient().functions.invoke<EmailRecipientPreview>(
          "send_email",
          {
            method: "POST",
            body: {
              mode: "preview",
              company_ids: companyIds,
              ...(valg?.recipients ? { recipients: valg.recipients } : {}),
            },
          },
        );
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke hente mottakerne"),
        );
      return data;
    },
    // 1:1-e-post til én kontakt (Macro-planen trinn 1). Lagres som
    // email_messages.kind = 'direct' og teller som kundekontakt når Mailgun
    // har akseptert den.
    async sendDirectEmail(
      contactId: Identifier,
      subject: string,
      body: string,
    ): Promise<{ sent: number; failed: number }> {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        sent: number;
        failed: number;
      }>("send_email", {
        method: "POST",
        body: { contact_id: contactId, subject, body },
      });
      if (!data || error)
        throw new Error(await invokeError(error, "Kunne ikke sende e-post"));
      return data;
    },
    // «Utkast med Claude» i e-postskriveren (neste release, punkt 3).
    // Returnerer ren tekst; ingenting sendes. Skriveprofilen leses på
    // serveren fra configuration.config.writingProfile.
    async draftEmail(input: {
      instruction: string;
      current: string;
      subject: string;
      recipientName?: string;
      recipientCompany?: string;
    }): Promise<string> {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        text: string;
      }>("draft_email", { method: "POST", body: input });
      if (!data?.text || error)
        throw new Error(await invokeError(error, "Klarte ikke å lage utkast"));
      return data.text;
    },
    async sendTestEmail(subject: string, body: string) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        sent: number;
        to: string;
      }>("send_email", {
        method: "POST",
        body: { mode: "test", subject, body },
      });
      if (!data || error) {
        const details = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw new Error(details?.message || "Kunne ikke sende testmelding");
      }
      return data;
    },
    // Hent avmeldte adresser (lowercase) så mottakertelleren i compose kan
    // trekke dem fra. Lite register i en single-org-CRM, så vi henter alt.
    async getEmailOptouts(): Promise<string[]> {
      const { data, error } = await getSupabaseClient()
        .from("email_optouts")
        .select("email");
      if (error) {
        throw new Error("Kunne ikke hente avmeldte adresser");
      }
      return (data ?? []).map((row) => row.email as string);
    },
    // Hent avsender/svar-til som e-poster sendes med (for visning i compose).
    async getEmailConfig() {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        from: string;
        replyTo: string;
        configured: boolean;
      }>("send_email", { method: "GET" });
      if (!data || error) {
        throw new Error("Kunne ikke hente e-postoppsett");
      }
      return data;
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        throw new Error("Failed to merge contacts");
      }

      return data;
    },
    // Dialog: opprett utsendelser med server-generert token for kundene som
    // ikke allerede har en ubesvart lenke i undersøkelsen (RPC).
    async createDialogInvitations(
      surveyId: Identifier,
      companyIds: Identifier[],
    ): Promise<DialogInvitation[]> {
      const { data, error } = await getSupabaseClient().rpc(
        "dialog_create_invitations",
        { p_survey_id: surveyId, p_company_ids: companyIds },
      );
      if (error) {
        throw new Error(error.message || "Kunne ikke generere lenker");
      }
      return (data ?? []) as DialogInvitation[];
    },
    // Gjennomgang av ufakturert arbeid (Macro-planen trinn 2): kjør nå.
    // Bare administrator; gir id-en til den nye kjøringen.
    // Lagrede spørsmål (neste release, punkt 4). Claude skriver spørringen
    // og prøvekjører den; ingenting lagres før brukeren trykker Lagre.
    async draftLiveQuestion(question: string): Promise<LiveQuestionDraft> {
      const { data, error } =
        await getSupabaseClient().functions.invoke<LiveQuestionDraft>(
          "live_question",
          { method: "POST", body: { question } },
        );
      if (!data || error)
        throw new Error(
          await invokeError(error, "Klarte ikke å lage spørringen"),
        );
      return data;
    },
    // Kjører et lagret spørsmål på nytt, som den lesende rollen og med
    // brukerens RLS (run_live_question).
    // Kundesiden (Living Customer Page). Faktagrunnlaget skrives bare
    // gjennom RPC-ene; forslag lages av edge-funksjonen propose_page_changes.
    async ensureCustomerPage(companyId: Identifier): Promise<number> {
      const { data, error } = await getSupabaseClient().rpc(
        "ensure_customer_page",
        { p_company_id: Number(companyId) },
      );
      if (error || data == null) {
        throw new Error(error?.message || "Kunne ikke åpne kundesiden");
      }
      return data as number;
    },
    async customerPageCoverage(companyId: Identifier): Promise<PageCoverage> {
      const { data, error } = await getSupabaseClient().rpc(
        "customer_page_coverage",
        { p_company_id: Number(companyId) },
      );
      if (error) throw new Error(error.message || "Kunne ikke hente dekningen");
      return data as PageCoverage;
    },
    async customerPageSource(
      companyId: Identifier,
      type: string,
      id: string,
    ): Promise<PageSource | null> {
      const { data, error } = await getSupabaseClient().rpc(
        "customer_page_source",
        { p_company_id: Number(companyId), p_type: type, p_id: id },
      );
      if (error) throw new Error(error.message || "Kunne ikke hente kilden");
      return (data as PageSource | null) ?? null;
    },
    async proposePageChanges(
      companyId: Identifier,
      section?: string | null,
    ): Promise<{ status: string; proposals: number; dropped?: number }> {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        status: string;
        proposals: number;
        dropped?: number;
      }>("propose_page_changes", {
        method: "POST",
        body: { company_id: Number(companyId), section: section ?? null },
      });
      if (!data || error) {
        throw new Error(await invokeError(error, "Oppdateringen feilet"));
      }
      return data;
    },
    async approvePageProposal(
      id: Identifier,
      edit: PageProposalEdit | null,
      attest: boolean,
      attestNote: string | null,
    ): Promise<ApproveProposalResult> {
      const { data, error } = await getSupabaseClient().rpc(
        "approve_page_proposal",
        {
          p_proposal_id: Number(id),
          p_edit: edit,
          p_attest: attest,
          p_attest_note: attestNote,
        },
      );
      if (error) throw new Error(error.message || "Kunne ikke godkjenne");
      return data as ApproveProposalResult;
    },
    async rejectPageProposal(id: Identifier, reason: string): Promise<void> {
      const { error } = await getSupabaseClient().rpc("reject_page_proposal", {
        p_proposal_id: Number(id),
        p_reason: reason,
      });
      if (error) throw new Error(error.message || "Kunne ikke avvise");
    },
    async attestPageItem(
      type: PageTargetType,
      id: Identifier,
      note: string | null,
    ): Promise<void> {
      const { error } = await getSupabaseClient().rpc("attest_page_item", {
        p_target_type: type,
        p_target_id: Number(id),
        p_note: note,
      });
      if (error) throw new Error(error.message || "Kunne ikke bekrefte");
    },
    async updateCustomerPageText(
      pageId: Identifier,
      internalNotes: string | null,
      instructions: string | null,
    ): Promise<void> {
      const { error } = await getSupabaseClient().rpc(
        "update_customer_page_text",
        {
          p_page_id: Number(pageId),
          p_internal_notes: internalNotes,
          p_instructions: instructions,
        },
      );
      if (error) throw new Error(error.message || "Kunne ikke lagre");
    },
    async createHumanProposal(
      pageId: Identifier,
      targetType: PageTargetType,
      targetId: Identifier | null,
      change: "add" | "update" | "close" | "remove",
      payload: PageProposalPayload,
    ): Promise<number> {
      const { data, error } = await getSupabaseClient().rpc(
        "create_human_proposal",
        {
          p_page_id: Number(pageId),
          p_target_type: targetType,
          p_target_id: targetId == null ? null : Number(targetId),
          p_change: change,
          p_payload: payload,
        },
      );
      if (error) throw new Error(error.message || "Kunne ikke lagre forslaget");
      return data as number;
    },
    async createTaskFromObligation(id: Identifier): Promise<number> {
      const { data, error } = await getSupabaseClient().rpc(
        "create_task_from_obligation",
        { p_obligation_id: Number(id) },
      );
      if (error) throw new Error(error.message || "Kunne ikke lage oppgaven");
      return data as number;
    },
    // Merket i kundelista: ventende forslag og poster som må sjekkes.
    // Viewet har ingen id-kolonne, så det leses rett fra Supabase.
    async customerPageStatuses(
      companyIds: Identifier[],
    ): Promise<CustomerPageStatus[]> {
      if (!companyIds.length) return [];
      const { data, error } = await getSupabaseClient()
        .from("customer_page_status")
        .select("*")
        .in("company_id", companyIds.map(Number));
      if (error) throw new Error(error.message);
      return (data ?? []) as CustomerPageStatus[];
    },
    async runLiveQuestion(id: Identifier): Promise<LiveQueryResult> {
      const { data, error } = await getSupabaseClient().rpc(
        "run_live_question",
        { p_id: Number(id) },
      );
      if (error) throw new Error(error.message || "Spørringen feilet");
      return data as LiveQueryResult;
    },
    async runUnbilledReviewNow(): Promise<number> {
      const { data, error } = await getSupabaseClient().rpc(
        "run_unbilled_review_now",
      );
      if (error) {
        throw new Error(error.message || "Kunne ikke kjøre gjennomgangen");
      }
      return data as number;
    },
    // Globalsøk (PR-S2): topptreff på tvers av kunder/kontakter/prosjekter/
    // oppgaver/salg/prospekter til Cmd+K-paletten. Semantikk som listesøkene.
    async globalSearch(q: string): Promise<GlobalSearchHit[]> {
      const { data, error } = await getSupabaseClient().rpc("global_search", {
        p_q: q,
      });
      if (error) {
        throw new Error(error.message || "Søket feilet");
      }
      return (data ?? []) as GlobalSearchHit[];
    },
    // Berik kunder som har org.nr men mangler bransje fra BRREG. Fyller kun
    // tomme felter (bransje/størrelse/adresse); edge-funksjonen krever
    // administrator. Trygg å kjøre flere ganger.
    async enrichCompaniesFromBrreg() {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        scanned: number;
        updated: number;
        skipped: number;
        notFound: number;
      }>("brreg", {
        method: "POST",
        body: { mode: "enrich_companies" },
      });
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke berike kunder fra BRREG"),
        );
      return data;
    },
    // Kundeskjemaets «Hent fra BRREG»: slå opp ett org.nr i Enhetsregisteret
    // og få rå feltverdier til skjema-utfylling. Skriver ingenting.
    async lookupBrreg(orgNr: string) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        org_nr: string;
        navn: string | null;
        website: string | null;
        email: string | null;
        phone: string | null;
        sector: string | null;
        size: number | null;
        address: string | null;
        zipcode: string | null;
        city: string | null;
        state: string | null;
        country: string | null;
        revenue: string | null;
      }>("brreg", {
        method: "POST",
        body: { mode: "lookup", org_nr: orgNr },
      });
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke slå opp i Enhetsregisteret"),
        );
      return data;
    },
    // Mimin 1.5 (P15-03): ordrekladd eller manuell markering per kunde
    // gjennom eksportjournalen. Klienten sender bare kunde, måned,
    // forventet grunnlagsversjon og forespørselsnøkkel; serveren beregner,
    // reserverer og sender. Svaret har ett utfall per kunde.
    async billingExport(input: {
      month: string;
      kind: ExportKind;
      orderDate?: string;
      deliveryDate?: string;
      customers: ExportRequest[];
      // Manuell markering: forklaring (påkrevd) og valgfri ekstern referanse.
      note?: string;
      externalReference?: string;
    }): Promise<ExportOutcome[]> {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        outcomes: ExportOutcome[];
      }>("tripletex", {
        method: "POST",
        body: {
          mode: "billing_export",
          period: { kind: "month", month: input.month },
          kind: input.kind,
          orderDate: input.orderDate,
          deliveryDate: input.deliveryDate,
          customers: input.customers,
          note: input.note,
          externalReference: input.externalReference,
        },
      });
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke sende fakturagrunnlaget"),
        );
      return data.outcomes;
    },
    // Avklar en eksport med ukjent utfall: knytt en bekreftet Tripletex-
    // ordre, eller dokumenter at ingen kladd finnes.
    async resolveBillingExport(input: {
      exportId: Identifier;
      outcome: "opprettet" | "feilet";
      orderId?: number | null;
      note: string;
    }): Promise<void> {
      const { error } = await getSupabaseClient().functions.invoke(
        "tripletex",
        { method: "POST", body: { mode: "billing_resolve", ...input } },
      );
      if (error)
        throw new Error(
          await invokeError(error, "Kunne ikke avklare eksporten"),
        );
    },
    // Korriger en opprettet eksport (P16-07): den blir stående som
    // «korrigert» med begrunnelse, og kildene frigjøres for ny sending.
    async correctBillingExport(input: {
      exportId: Identifier;
      note: string;
    }): Promise<void> {
      const { error } = await getSupabaseClient().functions.invoke(
        "tripletex",
        { method: "POST", body: { mode: "billing_correct", ...input } },
      );
      if (error)
        throw new Error(
          await invokeError(error, "Kunne ikke korrigere eksporten"),
        );
    },
    // Mimin 1.5 (P15-01): fakturagrunnlaget beregnes på serveren fra
    // komplette kilder, med instansens enhet og time-mva. Samme linjer
    // brukes i forhåndsvisning, CSV og ordrekladd.
    // Mimin 1.5 (P15-04): aktiver fakturajournalen med første journalmåned.
    // Én gang, bare administrator; databasen avviser alt annet.
    async activateBillingJournal(firstMonth: string): Promise<void> {
      const { error } = await getSupabaseClient().rpc(
        "billing_activate_journal",
        { p_first_month: firstMonth },
      );
      if (error) {
        throw new Error(
          error.message || "Kunne ikke aktivere fakturajournalen",
        );
      }
    },
    async billingPreview(period: BillingPeriod): Promise<BillingPreview> {
      const { data, error } =
        await getSupabaseClient().functions.invoke<BillingPreview>(
          "tripletex",
          { method: "POST", body: { mode: "billing_preview", period } },
        );
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke hente fakturagrunnlaget"),
        );
      return data;
    },
    // Tripletex er master for kunde-, kontakt- og produktinfo: hent
    // stamdata inn i Mimin (aldri motsatt vei). Admin-gatet i funksjonen.
    // preview = true gjør nøyaktig samme matching uten å skrive noe
    // («Sjekk match»-tørrkjøringen i synk-dialogen).
    async syncFromTripletex(preview = false) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        dryRun: boolean;
        customers: {
          created: number;
          updated: number;
          skipped: number;
          toCreate: string[];
        };
        contacts: { created: number; updated: number; skipped: number };
        products: {
          created: number;
          updated: number;
          skipped: number;
          deactivated: number;
          toCreate: string[];
          toDeactivate: string[];
        };
      }>("tripletex", {
        method: "POST",
        body: { mode: preview ? "sync_preview" : "sync" },
      });
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke synkronisere fra Tripletex"),
        );
      return data;
    },
    // Pakke 3 (Tapt og gjenåpning). Status lagres straks; automasjonene
    // legges i køen med 10 sekunders angrefrist. Gir jobb-id-en til Angre.
    async markDealLost(input: MarkDealLostInput): Promise<Identifier> {
      const { data, error } = await getSupabaseClient().rpc("mark_deal_lost", {
        p_deal_id: input.dealId,
        p_reason: input.reason,
        p_competitor_id: input.competitorId ?? null,
        p_comment: input.comment ?? null,
        p_learning: input.learning ?? null,
        p_revisit_date: input.revisitDate ?? null,
        p_nurture: input.nurture ?? false,
        p_open_tasks: input.openTasks ?? "complete",
        p_lost_date: input.lostDate ?? null,
      });
      if (error) {
        throw new Error(error.message || "Kunne ikke markere som tapt");
      }
      return data as Identifier;
    },
    // false betyr at fristen er ute og jobben har kjørt.
    async undoAutomationJob(jobId: Identifier): Promise<boolean> {
      const { data, error } = await getSupabaseClient().rpc(
        "undo_automation_job",
        { p_job_id: jobId },
      );
      if (error) throw new Error(error.message || "Kunne ikke angre");
      return !!data;
    },
    async retryAutomationJob(jobId: Identifier): Promise<boolean> {
      const { data, error } = await getSupabaseClient().rpc(
        "retry_automation_job",
        { p_job_id: jobId },
      );
      if (error) throw new Error(error.message || "Kunne ikke prøve igjen");
      return !!data;
    },
    async postponeDeal(input: PostponeDealInput): Promise<Identifier> {
      const { data, error } = await getSupabaseClient().rpc("postpone_deal", {
        p_deal_id: input.dealId,
        p_revisit_date: input.revisitDate,
        p_expected_close: input.expectedClose ?? null,
        p_title: input.title ?? null,
        p_note: input.note ?? null,
      });
      if (error) throw new Error(error.message || "Kunne ikke utsette");
      return data as Identifier;
    },
    async createDealFromLost(input: DealFromLostInput): Promise<Identifier> {
      const { data, error } = await getSupabaseClient().rpc(
        "create_deal_from_lost",
        {
          p_deal_id: input.dealId,
          p_next_step: input.nextStep,
          p_next_step_date: input.nextStepDate,
          p_next_step_type: input.nextStepType ?? "call",
          p_stage: input.stage ?? null,
        },
      );
      if (error) {
        throw new Error(error.message || "Kunne ikke lage ny mulighet");
      }
      return data as Identifier;
    },
    // Tilbudet bak en kundelenke (analysen v2, pakke E). Virker uten
    // innlogging; null når lenken er ukjent eller tilbudet er et utkast.
    async getPublicQuote(token: string): Promise<PublicQuote | null> {
      const { data, error } = await getSupabaseClient().rpc("quote_public", {
        p_token: token,
      });
      if (error) throw new Error(error.message || "Kunne ikke hente tilbudet");
      return (data as PublicQuote | null) ?? null;
    },
    // Pakke 4 (Vunnet og overlevering). Status, kunde og linjer lagres
    // straks; W1–W10 legges i køen med 10 sekunders angrefrist.
    async markDealWon(input: MarkDealWonInput): Promise<Identifier> {
      const { data, error } = await getSupabaseClient().rpc("mark_deal_won", {
        p_deal_id: input.dealId,
        p_input: {
          company_id: input.companyId ?? null,
          new_company: input.newCompany ?? null,
          tax_identifier: input.taxIdentifier ?? null,
          invoice_email: input.invoiceEmail ?? null,
          lines: input.lines,
          one_time_override: input.oneTimeOverride ?? null,
          override_reason: input.overrideReason ?? null,
          binding_months: input.bindingMonths ?? null,
          subscription_start: input.subscriptionStart ?? null,
          won_date: input.wonDate ?? null,
          acceptance_method: input.acceptanceMethod ?? "email",
          win_reason: input.winReason ?? null,
          project_lead_id: input.projectLeadId ?? null,
          template_id: input.templateId ?? null,
          planned_start: input.plannedStart ?? null,
          planned_launch: input.plannedLaunch ?? null,
          actions: input.actions ?? {},
          open_tasks: input.openTasks ?? "move",
          billing_owner_id: input.billingOwnerId ?? null,
          notify_ids: input.notifyIds ?? null,
          thanks: input.thanks ?? null,
          review_draft: input.reviewDraft ?? null,
        },
      });
      if (error) {
        throw new Error(error.message || "Kunne ikke markere som vunnet");
      }
      return data as Identifier;
    },
    async wonDealFootprint(dealId: Identifier): Promise<WonDealFootprint> {
      const { data, error } = await getSupabaseClient().rpc(
        "won_deal_footprint",
        { p_deal_id: dealId },
      );
      if (error) throw new Error(error.message || "Kunne ikke hente salget");
      return data as WonDealFootprint;
    },
    async cleanupWonDeal(
      input: CleanupWonDealInput,
    ): Promise<{ archived: number; deactivated: number }> {
      const { data, error } = await getSupabaseClient().rpc(
        "cleanup_won_deal",
        {
          p_deal_id: input.dealId,
          p_project: input.project,
          p_products: input.products,
        },
      );
      if (error) throw new Error(error.message || "Kunne ikke rydde salget");
      return data as { archived: number; deactivated: number };
    },
    // Pakke 5 (Lead inn og inntak): «Kjenner vi dem?», hurtigregistrering,
    // «Gjør til lead» på en inntaksoppgave og søk i Brønnøysund.
    async findLeadDuplicates(
      input: LeadDuplicateInput,
    ): Promise<LeadDuplicate[]> {
      const { data, error } = await getSupabaseClient().rpc(
        "find_lead_duplicates",
        { p_input: input },
      );
      if (error) {
        throw new Error(error.message || "Kunne ikke sjekke duplikater");
      }
      return (data ?? []) as LeadDuplicate[];
    },
    async createLead(input: CreateLeadInput): Promise<CreateLeadResult> {
      const { data, error } = await getSupabaseClient().rpc("create_lead", {
        p_input: input,
      });
      if (error) throw new Error(error.message || "Kunne ikke lagre leaden");
      return data as CreateLeadResult;
    },
    async taskToLead(workItemId: Identifier): Promise<Identifier> {
      const { data, error } = await getSupabaseClient().rpc("task_to_lead", {
        p_work_item_id: workItemId,
      });
      if (error) {
        throw new Error(error.message || "Kunne ikke gjøre oppgaven til lead");
      }
      return data as Identifier;
    },
    async searchBrreg(query: string): Promise<BrregSearchHit[]> {
      const { data, error } = await getSupabaseClient().functions.invoke<
        BrregSearchHit[] | { results?: BrregSearchHit[] }
      >("brreg", {
        method: "POST",
        body: { mode: "search", query },
      });
      if (error)
        throw new Error(
          await invokeError(error, "Kunne ikke søke i Enhetsregisteret"),
        );
      if (Array.isArray(data)) return data;
      return data?.results ?? [];
    },
    // Gjør et prospekt (brreg_entity) til en kunde i companies + en deal i
    // «Mulighet»-kolonnen (PR-3). Idempotent i databasefunksjonen: dedup på
    // org.nr for kunden, og en åpen deal på kunden gjenbrukes.
    async promoteProspect(
      orgNr: string,
    ): Promise<{ company_id: number; deal_id: number }> {
      const { data, error } = await getSupabaseClient().rpc(
        "promote_prospect_by_org",
        { p_org_nr: orgNr },
      );
      if (error) {
        throw new Error(
          error.message || "Kunne ikke gjøre prospektet til kunde",
        );
      }
      return data as { company_id: number; deal_id: number };
    },
    // Kundens neste steg (P16-01): et åpent steg lukkes som erstattet, og det
    // nye returneres. Uten tekst lagres «ingen planlagt steg» med begrunnelse.
    async setCompanyNextStep(input: NextStepInput): Promise<CompanyNextStep> {
      const { data, error } = await getSupabaseClient().rpc(
        "set_company_next_step",
        {
          p_company_id: input.companyId,
          p_description: input.description ?? null,
          p_owner_id: input.ownerId ?? null,
          p_due_date: input.dueDate || null,
          p_no_step_reason: input.noStepReason ?? null,
        },
      );
      if (error) {
        throw new Error(
          error.code === "23505"
            ? "Noen andre endret neste steg samtidig. Last inn siden og prøv igjen."
            : error.message || "Kunne ikke lagre neste steg",
        );
      }
      return data as CompanyNextStep;
    },
    // «Registrer kontakt» fra Oversikt (Oversikt «Fokus», fiks 1): én kontakt
    // i kontaktloggen og høyst ett neste steg, i én transaksjon.
    async logContact(input: LogContactInput): Promise<LogContactResult> {
      const { data, error } = await getSupabaseClient().rpc("log_contact", {
        p_company_id: input.companyId,
        p_type: input.type,
        p_note: input.note || null,
        p_next_step: input.nextStep || null,
        p_due: input.due || null,
        p_deal_id: input.dealId ?? null,
      });
      if (error) {
        throw new Error(error.message || "Kunne ikke registrere kontakten");
      }
      return data as LogContactResult;
    },
    // Planlagt arbeidsuke (Team på Oversikt). Administrator for alle, ellers
    // bare egen.
    async setPlannedWeekHours(salesId: Identifier, hours: number) {
      const { error } = await getSupabaseClient().rpc(
        "set_planned_week_hours",
        { p_sales_id: salesId, p_hours: hours },
      );
      if (error) {
        throw new Error(error.message || "Kunne ikke lagre planlagt uke");
      }
    },
    // Fullfør et neste steg. Trygt å kalle to ganger.
    async completeCompanyNextStep(id: Identifier): Promise<CompanyNextStep> {
      const { data, error } = await getSupabaseClient().rpc(
        "complete_company_next_step",
        { p_id: id },
      );
      if (error) {
        throw new Error(error.message || "Kunne ikke fullføre neste steg");
      }
      return data as CompanyNextStep;
    },
    // Fremdriften i veiledningen og oppstartskortet for den innloggede
    // brukeren (PRD US23.4). Null når brukeren ikke har noen rad ennå.
    async getTutorialProgress(): Promise<string[] | null> {
      const { data, error } = await getSupabaseClient()
        .from("tutorial_progress")
        .select("completed")
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data ? (data.completed as string[]) : null;
    },
    async saveTutorialProgress(completed: string[]): Promise<void> {
      const { error } = await getSupabaseClient()
        .from("tutorial_progress")
        .upsert(
          { completed, updated_at: new Date().toISOString() },
          { onConflict: "user_id" },
        );
      if (error) throw new Error(error.message);
    },
    // Trygg timer (P16-06): start stopper en eventuell løpende timer i samme
    // transaksjon; stopp er idempotent og beholder første stopptid.
    async startTimeEntry(
      workItemId: Identifier,
      billable: boolean,
    ): Promise<TimeEntry> {
      const { data, error, status } = await getSupabaseClient().rpc(
        "start_time_entry",
        { p_work_item_id: workItemId, p_billable: billable },
      );
      if (error) throw timerError(error, status, "Kunne ikke starte timeren");
      return data as TimeEntry;
    },
    async stopTimeEntry(id: Identifier, endedAt: string): Promise<TimeEntry> {
      const { data, error, status } = await getSupabaseClient().rpc(
        "stop_time_entry",
        { p_id: id, p_ended_at: endedAt },
      );
      if (error) throw timerError(error, status, "Kunne ikke stoppe timeren");
      return data as TimeEntry;
    },
    // Endre et egendefinert felt og flytt verdiene eksplisitt (P16-05).
    async changeFieldDef(
      input: FieldDefChangeInput,
    ): Promise<FieldDefChangeResult> {
      const { data, error } = await getSupabaseClient().rpc(
        "change_field_def",
        {
          p_id: input.id,
          p_name: input.name,
          p_type: input.type,
          p_options: input.type === "select" ? input.options : null,
          p_hidden: input.hidden,
          p_mapping: input.mapping ?? {},
          p_clear_invalid: input.clearInvalid ?? false,
        },
      );
      if (error) {
        throw new Error(error.message || "Kunne ikke lagre feltet");
      }
      return data as FieldDefChangeResult;
    },
    // Sett status på et prospekt (f.eks. 'dismissed' for å ignorere, 'new' for
    // å vise igjen). Oppretter prospects-raden ved behov.
    async setProspectStatus(orgNr: string, status: string): Promise<void> {
      const { error } = await getSupabaseClient().rpc("set_prospect_status", {
        p_org_nr: orgNr,
        p_status: status,
      });
      if (error) {
        throw new Error(error.message || "Kunne ikke oppdatere prospektstatus");
      }
    },
    // Bekreft eller korriger nettstedet på et prospekt manuelt (P2). Tom
    // url = «denne siden tilhører ikke dem» og kobler nettstedet fra. En
    // manuell bekreftelse overstyrer den automatiske verifiseringen.
    async setProspectUrl(orgNr: string, url: string | null): Promise<void> {
      const { error } = await getSupabaseClient().rpc("set_prospect_url", {
        p_org_nr: orgNr,
        p_url: url,
      });
      if (error) {
        throw new Error(error.message || "Kunne ikke lagre nettstedet");
      }
    },
    // Sett kilde på et prospekt: hvor kom det fra. «BRREG» og «Proff» settes
    // automatisk der raden oppstår; denne veien finnes for «Henvisning» og
    // «E-post», som ingen automatikk kan vite. null nullstiller.
    async setProspectKilde(orgNr: string, kilde: string | null): Promise<void> {
      const { error } = await getSupabaseClient().rpc("set_prospect_kilde", {
        p_org_nr: orgNr,
        p_kilde: kilde,
      });
      if (error) {
        throw new Error(error.message || "Kunne ikke lagre kilde");
      }
    },
    // Lagre notat på et prospekt (oppretter prospects-raden ved behov).
    async setProspectNotes(orgNr: string, notes: string): Promise<void> {
      const { error } = await getSupabaseClient().rpc("set_prospect_notes", {
        p_org_nr: orgNr,
        p_notes: notes,
      });
      if (error) {
        throw new Error(error.message || "Kunne ikke lagre notat");
      }
    },
    // P16-10 (US24.1): eier (auth-bruker), oppfølgingsdato og
    // avslutningsårsak. Null tømmer feltet.
    async setProspectFollowup(
      orgNr: string,
      input: {
        owner: string | null;
        followUpDate: string | null;
        closeReason: string | null;
      },
    ): Promise<void> {
      const { error } = await getSupabaseClient().rpc("set_prospect_followup", {
        p_org_nr: orgNr,
        p_owner: input.owner,
        p_follow_up_date: input.followUpDate,
        p_close_reason: input.closeReason,
      });
      if (error) {
        throw new Error(error.message || "Kunne ikke lagre oppfølgingen");
      }
    },
    // edge-funksjonens oppsummering. Kalles sekvensielt fra UI.
    async fetchBrregProspects(
      kommunenr: string,
      naeringskoder: string[],
      limit: number,
    ) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        fetched: number;
        upserted: number;
        skipped_kommune: number;
        skipped_store: number;
        skipped_dod: number;
      }>("brreg", {
        method: "POST",
        body: { kommunenr, naeringskoder, limit },
      });
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke hente fra Brønnøysund"),
        );
      return data;
    },
    // Hvor stort er universet, og hvor mange enheter mangler opplysninger fra
    // Enhetsregisteret? Det siste tallet driver berikelsesbanneret.
    async brregProspectStatus() {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        count: number;
        last_synced: string | null;
        uberiket: number;
      }>("brreg", { method: "GET" });
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke hente status fra Brønnøysund"),
        );
      return data;
    },
    // Fasett-tellingene til filtermenyen på Prospekter (etappe 3).
    //
    // Menyen utledet før både valgene og tallene fra de hentede radene. Med
    // paginering finnes de ikke i klienten lenger, og en meny bygget på 50
    // rader ville tilbudt fire kommuner der universet har 230. Tallene gjelder
    // hele universet og tar ikke hensyn til aktive filtre — se funksjonens
    // kommentar i migrasjonen for hvorfor det er den nyttige semantikken.
    // A1 fra leadsimport-runbooken, men fra NETTLESEREN: skriv én porsjon rader
    // fra Proff-fila til brreg_entities.
    //
    // Stegene og rekkefølgen er de samme fem som importer-leads.mjs gjør, og
    // grunnen til hvert av dem står der. Kort:
    //
    //   1. upsert, gruppert på utfylte felt — et `felt: null` i payloaden ville
    //      blitt `set felt = null` for radene som alt finnes (#261-feilen).
    //   2. kilde = 'proff' bare der ingen kilde står; BRREG-radene beholder sin.
    //   3. url bare der ingen adresse står — vernet mot at fila vasker bort en
    //      adresse resolveren fant eller et menneske rettet.
    //   4-5. stiftelsesdato og mva bare der de mangler; Enhetsregisteret er
    //      førstehånds og beholder forrangen.
    //
    // Porsjonsvis med vilje: kalleren teller fremdrift, og en avbrutt import kan
    // tas opp igjen ved å kjøre den om igjen — alle fem stegene er idempotente.
    async importerProffRader(rader: ProffRad[]) {
      const klient = getSupabaseClient();
      const orgNr = rader.map((r) => r.org_nr);
      const resultat = {
        upsertet: 0,
        kildeSatt: 0,
        urlSatt: 0,
        datoSatt: 0,
        mvaSatt: 0,
      };

      // 1) Upsert. grupperPaaUtfylteFelt deles med skriptet — se den.
      for (const gruppe of grupperPaaUtfylteFelt(rader.map(radForUpsert))) {
        const { error } = await klient
          .from("brreg_entities")
          .upsert(gruppe, { onConflict: "org_nr" });
        if (error)
          throw new Error(`Kunne ikke skrive radene: ${error.message}`);
        resultat.upsertet += gruppe.length;
      }

      // 2) Kilde.
      const kilde = await klient
        .from("brreg_entities")
        .update({ kilde: "proff" })
        .in("org_nr", orgNr)
        .is("kilde", null)
        .select("org_nr");
      if (kilde.error) {
        throw new Error(`Kunne ikke sette kilde: ${kilde.error.message}`);
      }
      resultat.kildeSatt += kilde.data?.length ?? 0;

      // 3) Nettadressen. Én oppdatering per rad, fordi verdien er forskjellig
      // for hver — dette kan ikke slås sammen slik de andre stegene kan.
      const medUrl = rader.filter((r) => r.url);
      for (let i = 0; i < medUrl.length; i += 20) {
        const svar = await Promise.all(
          medUrl.slice(i, i + 20).map((r) =>
            klient
              .from("brreg_entities")
              .update({
                url: r.url,
                url_kilde: "proff",
                // Under BRREGs 1: adressen er annenhånds. Proff har registrert
                // den, virksomheten har ikke oppgitt den til oss.
                url_confidence: 0.9,
              })
              .eq("org_nr", r.org_nr)
              .is("url", null)
              .select("org_nr"),
          ),
        );
        for (const { data, error } of svar) {
          if (error) {
            throw new Error(`Kunne ikke lagre nettadresse: ${error.message}`);
          }
          resultat.urlSatt += data?.length ?? 0;
        }
      }

      // 4-5) Stiftelsesdato og mva, gruppert på VERDI: stiftelsesdatoen har
      // tusenvis av distinkte verdier, mva har to. Uten gruppering ville dette
      // vært ett kall per rad for ingenting.
      const derTomt = async (
        felt: "stiftelsesdato" | "registrert_mva",
        verdier: { org_nr: string; verdi: string | boolean | null }[],
      ) => {
        const grupper = new Map<string, { verdi: unknown; orgNr: string[] }>();
        for (const { org_nr, verdi } of verdier) {
          if (verdi === null || verdi === undefined) continue;
          const n = String(verdi);
          if (!grupper.has(n)) grupper.set(n, { verdi, orgNr: [] });
          grupper.get(n)!.orgNr.push(org_nr);
        }
        let satt = 0;
        const kall = [...grupper.values()];
        for (let i = 0; i < kall.length; i += 20) {
          const svar = await Promise.all(
            kall.slice(i, i + 20).map(({ verdi, orgNr: bit }) =>
              klient
                .from("brreg_entities")
                .update({ [felt]: verdi })
                .in("org_nr", bit)
                .is(felt, null)
                .select("org_nr"),
            ),
          );
          for (const { data, error } of svar) {
            if (error) {
              throw new Error(`Kunne ikke skrive ${felt}: ${error.message}`);
            }
            satt += data?.length ?? 0;
          }
        }
        return satt;
      };

      resultat.datoSatt += await derTomt(
        "stiftelsesdato",
        rader.map((r) => ({ org_nr: r.org_nr, verdi: r.stiftelsesdato })),
      );
      resultat.mvaSatt += await derTomt(
        "registrert_mva",
        rader.map((r) => ({ org_nr: r.org_nr, verdi: r.registrert_mva })),
      );
      return resultat;
    },
    // Hvilke av disse org.numrene som ALLEREDE ligger i universet. Brukes av
    // tørrkjøringen for å skille «nye» fra «oppdaterer eksisterende» — samme
    // tall skriptet viser før det skriver.
    async prospektFinnesFraFor(orgNr: string[]): Promise<string[]> {
      const funnet: string[] = [];
      for (let i = 0; i < orgNr.length; i += 500) {
        const { data, error } = await getSupabaseClient()
          .from("brreg_entities")
          .select("org_nr")
          .in("org_nr", orgNr.slice(i, i + 500));
        if (error) {
          throw new Error(`Kunne ikke slå opp org.numre: ${error.message}`);
        }
        funnet.push(...(data ?? []).map((r) => r.org_nr as string));
      }
      return funnet;
    },
    async prospektFasetter(): Promise<Fasetter> {
      const { data, error } =
        await getSupabaseClient().rpc("prospekt_fasetter");
      if (error) {
        throw new Error(error.message || "Kunne ikke hente fasett-tellinger");
      }
      return { ...TOMME_FASETTER, ...((data ?? {}) as Partial<Fasetter>) };
    },
    // A2 i leadsimport-runbooken: berik enheter som alt ligger i universet,
    // nøklet på org.nr. Tar én batch per kall og forteller hvor mange som
    // gjenstår, så kalleren kjører runder til gjenstaar er 0.
    async enrichProspectEntities(limit = 60, paaNytt = false) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        behandlet: number;
        beriket: number;
        ikke_funnet: number;
        feil: number;
        dode: number;
        gjenstaar: number;
        dekning: {
          kommunenr: number;
          antall_ansatte: number;
          vedtektsfestet_formaal: number;
          aktivitet: number;
        };
      }>("brreg", {
        method: "POST",
        body: { mode: "enrich_entities", limit, paa_nytt: paaNytt },
      });
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke berike fra Brønnøysund"),
        );
      return data;
    },
    // paaNytt=true tar med enheter vi allerede har gitt opp på (P12).
    //
    // Tom `kommunenr` betyr HELE universet. Edge-funksjonen legger bare på
    // kommunefilteret når verdien ser ut som et kommunenummer
    // (`resolve_urls/index.ts`), så «» er ikke et hull — det er den
    // dokumenterte måten å be om alle radene. Søkedialogen sender en kommune
    // fordi den nettopp hentet den; banneret gjør det ikke, fordi radene der
    // kan ligge i 230 forskjellige kommuner.
    async resolveProspectUrls(
      kommunenr: string,
      limit: number,
      paaNytt = false,
    ) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        processed: number;
        resolved: number;
      }>("resolve_urls", {
        method: "POST",
        body: { kommunenr, limit, paa_nytt: paaNytt },
      });
      if (!data || error)
        throw new Error(await invokeError(error, "Kunne ikke finne nettsider"));
      return data;
    },
    // Dypskann ett prospekt (Lighthouse + WCAG via PageSpeed Insights).
    // url/navn er fallback for kundeskann (V3) på enheter utenfor BRREG-basen.
    async deepScanProspect(orgNr: string, url?: string | null, navn?: string) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        score: number;
        performance: number | null;
        accessibility: number | null;
        seo: number | null;
        wcag_fails: number;
      }>("scan_deep", {
        method: "POST",
        body: { org_nr: orgNr, url: url ?? undefined, navn },
      });
      if (!data || error)
        throw new Error(await invokeError(error, "Dypskann feilet"));
      return data;
    },
    // rescan=true skanner enheter som allerede har en scan på nytt. Uten den
    // slår en forbedret scan-regel aldri inn på det som alt er skannet.
    // offset gjelder kun rescan: der er utvalget stabilt, så kalleren må
    // flytte vinduet selv for ikke å skanne de samme radene om igjen.
    async scanProspects(
      kommunenr: string,
      limit: number,
      rescan = false,
      offset = 0,
    ) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        processed: number;
        scored: number;
        avg_score: number;
      }>("scan_light", {
        method: "POST",
        body: { kommunenr, limit, rescan, offset },
      });
      if (!data || error)
        throw new Error(
          await invokeError(error, "Kunne ikke scanne nettsteder"),
        );
      return data;
    },
    // Kundeskann (V3): scan én kundes nettside via scan_light. Kundens
    // nettside/navn sendes med som fallback for kunder utenfor BRREG-basen.
    async scanCustomerSite(orgNr: string, url?: string | null, navn?: string) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        processed: number;
        scored: number;
        message?: string;
      }>("scan_light", {
        method: "POST",
        body: { org_nr: orgNr, url: url ?? undefined, navn },
      });
      if (!data || error)
        throw new Error(await invokeError(error, "Kundeskann feilet"));
      return data;
    },
    // Pipeline pakke 7 (AK44): hva lagringstidsjobben ville slettet nå, og
    // siste kjøring. Bare administrator (sjekkes i databasen).
    async retentionPreview(): Promise<RetentionPreview> {
      const { data, error } =
        await getSupabaseClient().rpc("retention_preview");
      if (error) {
        throw new Error(error.message || "Kunne ikke hente lagringstid");
      }
      return data as RetentionPreview;
    },
    // Pipeline pakke 7 (kap. 13.4): e-postoppsummeringen for innlogget
    // bruker. sales.today_digest kan ikke skrives direkte av brukeren.
    async setTodayDigest(enabled: boolean): Promise<void> {
      const { error } = await getSupabaseClient().rpc("set_today_digest", {
        p_enabled: enabled,
      });
      if (error) {
        throw new Error(
          error.message || "Kunne ikke lagre e-postoppsummeringen",
        );
      }
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const [{ data }, stages] = await Promise.all([
        baseDataProvider.getOne("configuration", { id: 1 }),
        // Stadiene ligger i deal_stages (pipeline v2.1), ikke i
        // konfigurasjons-JSON-en. Feiler oppslaget, brukes standardstadiene.
        getSupabaseClient()
          .from("deal_stages")
          .select("*")
          .eq("pipeline_id", 1)
          .order("position"),
      ]);
      const config = { ...((data?.config as ConfigurationContextValue) ?? {}) };
      delete (config as Partial<ConfigurationContextValue>).dealStages;
      if (!stages.error && stages.data?.length) {
        config.dealStages = (stages.data as DealStageRow[]).map(
          dealStageFromRow,
        );
      }
      return config;
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      // Stadiene lagres i deal_stages via Innstillinger → Pipeline.
      const { dealStages, ...rest } = config;
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config: rest },
        previousData: { id: 1 },
      });
      return { ...(data.config as ConfigurationContextValue), dealStages };
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    // Vedlegg på oppgaver: samme opplasting til den private bøtta som
    // notatene. Sletting ryddes av databasetriggeren (oppgavevedlegg.sql).
    resource: "work_items",
    beforeSave: async (data: WorkItem, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    // Prospektlista søker mot serveren (etappe 3). Kolonnene er de samme fire
    // som klienten filtrerte på da søket skjedde i nettleseren — navn,
    // org.nr, næringstekst og aktivitet — så et søk gir samme treff som før,
    // bare over hele universet i stedet for de 500 hentede radene.
    resource: "prospect_overview",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "navn",
        "org_nr",
        "naeringskode_tekst",
        "aktivitet",
      ])(params);
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      // email_fts/phone_fts er genererte tekstkolonner på contacts_summary
      // (utflatet email_jsonb/phone_jsonb), søkbare med ilike.
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email_fts",
        "phone_fts",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "sector",
        "description",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
        "customer_number",
        "tax_identifier",
      ])(params);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      // company_name er med fordi autocomplete-referanser ofte søkes med
      // firmaet man husker, ikke personen.
      return applyFullTextSearch(["first_name", "last_name", "company_name"])(
        params,
      );
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
    // annual_value regnes av databasen (generert kolonne) og kan ikke
    // skrives; et skjema sender ellers hele raden tilbake.
    beforeSave: async (data) => {
      const { annual_value: _annual, ...rest } = data;
      return rest;
    },
  },
  // PR-S1: disse tre hadde SearchInput i listene, men manglet søkeoppsett —
  // q gikk rått til PostgREST som kolonnefilter og ga 400 i prod.
  {
    resource: "projects",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "description"])(params);
    },
  },
  {
    resource: "work_items",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["title", "description"])(params);
    },
  },
  {
    resource: "sales",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name", "email"])(params);
    },
  },
  {
    resource: "products",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "product_number", "description"])(
        params,
      );
    },
    // Produktbildet lastes opp til attachments-bucketen før lagring (samme
    // mønster som kundelogoen).
    beforeCreate: async (params) => {
      return await processProductImage(params);
    },
    beforeUpdate: async (params) => {
      return await processProductImage(params);
    },
  },
];

// Feilinnsikt (P16-09): feil får en referanse og logges i error_log.
export const errorReporter = createErrorReporter(() => {
  const client = getSupabaseClient();
  return client.rpc.bind(client) as unknown as Rpc;
});

let globalHandlersInstalled = false;
// Ukontrollerte feil i appen (ikke fra dataprovideren) logges også. De vises
// allerede av feilsiden eller ikke i det hele tatt; her gjelder det bare å
// kunne finne dem igjen.
const installGlobalErrorHandlers = () => {
  if (globalHandlersInstalled || typeof window === "undefined") return;
  globalHandlersInstalled = true;
  const ignore = /ResizeObserver loop|Script error\.?$/;
  window.addEventListener("error", (event) => {
    const message = event.message || String(event.error ?? "");
    if (!message || ignore.test(message)) return;
    errorReporter.report({
      category: classifyError(event.error ?? message),
      source: "klient: ukontrollert",
      message,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as { message?: string; ref?: string };
    if (reason?.ref) return; // Allerede logget av dataprovideren.
    const message = reason?.message ?? String(reason ?? "");
    if (!message || ignore.test(message)) return;
    errorReporter.report({
      category: classifyError(event.reason),
      source: "klient: ukontrollert",
      message,
    });
  });
};

export const getDataProvider = () => {
  if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error("Please set the VITE_SUPABASE_URL environment variable");
  }
  if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
    );
  }
  installGlobalErrorHandlers();
  return withErrorInsight(
    withPrivateCosts(
      withLifecycleCallbacks(
        getDataProviderWithCustomMethods(),
        lifeCycleCallbacks,
      ) as CrmDataProvider,
      getSupabaseClient as unknown as () => CostClient,
    ),
    errorReporter,
  );
};

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const fileParts = file.name.split(".");
  const fileExt = fileParts.length > 1 ? `.${file.name.split(".").pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
